import HtmlSignedCookieApi from "@apis/html.signed.cookie.api.ts";
import { cookieManager } from "@utils/cookie.manager.ts";

const REUSE_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

const CLEAR_TIMEOUT_MS = 5000;

type SignedCookieRequest = (token: string, timeout?: number) => Promise<void>;

interface HtmlSignedCookieDependencies {
  getToken: () => string | null | undefined;
  requestSignedCookie: SignedCookieRequest;
  deleteSignedCookie: SignedCookieRequest;
  now?: () => number;
  delay?: (ms: number) => Promise<void>;
}

export interface HtmlSignedCookieClient {
  ensure: () => Promise<void>;
  clear: () => Promise<void>;
}

export class MissingApplicationJwtError extends Error {
  constructor() {
    super("Access Denied: No jwt token found");
    this.name = "MissingApplicationJwtError";
  }
}

export class HtmlSignedCookieClearedError extends Error {
  constructor() {
    super("HTML signed cookie is being cleared");
    this.name = "HtmlSignedCookieClearedError";
  }
}

export const isMissingApplicationJwtError = (error: unknown): boolean =>
  error instanceof MissingApplicationJwtError;

export const isHtmlSignedCookieClearedError = (error: unknown): boolean =>
  error instanceof HtmlSignedCookieClearedError;

export const createHtmlSignedCookieClient = (
  dependencies: HtmlSignedCookieDependencies,
): HtmlSignedCookieClient => {
  let inFlightMint: Promise<void> | null = null;
  let inFlightClear: Promise<void> | null = null;
  let lastSuccessAt: number | null = null;

  let clearedForToken: string | null = null;
  const now = dependencies.now ?? ((): number => Date.now());
  const delay =
    dependencies.delay ??
    ((ms: number): Promise<void> =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
      }));

  const sendAuthorized = async (send: SignedCookieRequest, timeout?: number): Promise<void> => {
    const token = dependencies.getToken();
    if (!token) {
      throw new MissingApplicationJwtError();
    }

    await send(token, timeout);
  };

  const ensure = (): Promise<void> => {
    if (inFlightClear) {
      return Promise.reject(new HtmlSignedCookieClearedError());
    }

    if (clearedForToken !== null) {
      if (dependencies.getToken() === clearedForToken) {
        return Promise.reject(new HtmlSignedCookieClearedError());
      }
      clearedForToken = null;
    }

    if (inFlightMint) {
      return inFlightMint;
    }

    if (lastSuccessAt !== null && now() - lastSuccessAt < REUSE_WINDOW_MS) {
      return Promise.resolve();
    }

    inFlightMint = sendAuthorized(dependencies.requestSignedCookie)
      .then(() => {
        lastSuccessAt = now();
      })
      .finally(() => {
        inFlightMint = null;
      });

    return inFlightMint;
  };

  const clear = (): Promise<void> => {
    if (inFlightClear) {
      return inFlightClear;
    }

    clearedForToken = dependencies.getToken() ?? null;

    const settledMint = inFlightMint
      ? Promise.race([inFlightMint.catch(() => undefined), delay(CLEAR_TIMEOUT_MS)])
      : Promise.resolve();

    inFlightClear = settledMint
      .then(() => {
        lastSuccessAt = null;

        return sendAuthorized(dependencies.deleteSignedCookie, CLEAR_TIMEOUT_MS);
      })
      .finally(() => {
        inFlightClear = null;
      });

    return inFlightClear;
  };

  return { ensure, clear };
};

const htmlSignedCookieClient = createHtmlSignedCookieClient({
  getToken: () => cookieManager.getCookie("jwt_token"),
  requestSignedCookie: (token, timeout) => HtmlSignedCookieApi.requestSignedCookie(token, timeout),
  deleteSignedCookie: (token, timeout) => HtmlSignedCookieApi.deleteSignedCookie(token, timeout),
});

export const useHtmlSignedCookie = (): HtmlSignedCookieClient => htmlSignedCookieClient;
